import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Slider,
  TextField,
  Button as MuiButton,
} from '@mui/material';
import axiosInstant from '../../../api';
import config from '../../../config';
import { adminScoresSettings } from '../../../api/endpoints';
import { Button } from '../../../components/Button/Button';
import {
  AffinityBands,
  AFFINITY_GRADES,
  GRADE_META,
  boundariesFromBands,
  bandsFromBoundaries,
  boundariesEqual,
  round3,
} from './bands';
import { TIER_COLORS } from './AffinityNetwork/affinity.constants';

interface Props {
  affinityBands: AffinityBands;
  onAfterSave: () => void;
}

const settingsUrl = `${config.API_URL}${adminScoresSettings}`;

// Keeps a boundary strictly inside (0,1) and ordered against its neighbours so a
// fine-tune input can't invert the partition (the slider already disables swap).
const EPS = 0.001;

export const AffinityBandsCard: React.FC<Props> = ({ affinityBands, onAfterSave }) => {
  const original = useMemo(() => boundariesFromBands(affinityBands), [affinityBands]);
  const [boundaries, setBoundaries] = useState<number[]>(original);
  const [saving, setSaving] = useState(false);

  // Re-sync working state whenever the loaded baseline changes (after a save/refetch).
  useEffect(() => {
    setBoundaries(boundariesFromBands(affinityBands));
  }, [affinityBands]);

  const dirty = !boundariesEqual(boundaries, original);
  const bands = useMemo(() => bandsFromBoundaries(boundaries), [boundaries]);

  const handleSlider = (_: Event, value: number | number[]) => {
    if (Array.isArray(value)) setBoundaries(value.map(round3));
  };

  // Two-way numeric fine-tune: editing boundary i clamps it between its neighbours.
  const setBoundaryAt = (index: number, raw: string) => {
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    setBoundaries((prev) => {
      const next = [...prev];
      const lower = index === 0 ? 0 : next[index - 1];
      const upper = index === next.length - 1 ? 1 : next[index + 1];
      next[index] = round3(Math.min(upper - EPS, Math.max(lower + EPS, v)));
      return next;
    });
  };

  const reset = () => setBoundaries(original);

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstant.patch(settingsUrl, { affinityBands: bands });
      toast.success('Affinity bands saved — run a recompute to apply them to scores');
      onAfterSave();
    } catch (err) {
      toast.error('Failed to save affinity bands');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box className="flex items-center justify-between pb-1">
        <Typography fontWeight={600}>Affinity bands</Typography>
        <MuiButton size="small" variant="outlined" onClick={reset} disabled={!dirty}>
          Reset
        </MuiButton>
      </Box>
      <Typography color="var(--text-2)" sx={{ fontSize: 14, pb: 2 }}>
        Affinity (how well an expert&apos;s trade matches a product&apos;s category) is
        the primary ranking signal. It carves the [0,1] score range into four
        contiguous bands; attribute-fit × experience (A·Q) only fills each
        band&apos;s width. Drag the dividers to resize the bands — they always tile
        the full range.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Stacked colored band bar — segment widths are the live band widths. */}
      <Box
        className="flex w-full overflow-hidden"
        sx={{ height: 56, borderRadius: 1.5, mb: 1 }}
      >
        {AFFINITY_GRADES.map((grade) => {
          const meta = GRADE_META[grade];
          const w = bands[grade].width;
          return (
            <Box
              key={grade}
              sx={{
                width: `${w * 100}%`,
                bgcolor: TIER_COLORS[meta.tier],
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 0,
                overflow: 'hidden',
                transition: 'width 0.05s linear',
                borderRight: '1px solid rgba(255,255,255,0.6)',
              }}
            >
              {w > 0.08 && (
                <>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                    {meta.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11, opacity: 0.9 }}>
                    {round3(bands[grade].band)}–{round3(bands[grade].band + w)}
                  </Typography>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {/* The 3 draggable internal boundaries (disableSwap keeps them ordered). */}
      <Box sx={{ px: 0.5 }}>
        <Slider
          value={boundaries}
          onChange={handleSlider}
          min={0}
          max={1}
          step={0.005}
          disableSwap
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => round3(v).toString()}
          marks={[
            { value: 0, label: '0' },
            { value: 1, label: '1.0' },
          ]}
          sx={{ color: 'var(--accent)' }}
          aria-label="Affinity band boundaries"
        />
      </Box>

      {/* Per-band readout + numeric fine-tune for the editable floors. */}
      <Box className="flex flex-wrap gap-4 pt-2">
        {AFFINITY_GRADES.map((grade, i) => {
          const meta = GRADE_META[grade];
          const entry = bands[grade];
          // grade '0' floor is pinned at 0; the other three floors are boundary[i-1].
          const editableIndex = i - 1;
          return (
            <Box
              key={grade}
              className="flex flex-col gap-1"
              sx={{ minWidth: 150, flex: '1 1 150px' }}
            >
              <Box className="flex items-center gap-2">
                <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: TIER_COLORS[meta.tier] }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{meta.label}</Typography>
              </Box>
              <Typography color="var(--text-2)" sx={{ fontSize: 12 }}>
                {meta.sublabel} · width {round3(entry.width)}
              </Typography>
              {editableIndex >= 0 ? (
                <TextField
                  size="small"
                  type="number"
                  label="Floor"
                  value={entry.band}
                  onChange={(e) => setBoundaryAt(editableIndex, e.target.value)}
                  inputProps={{ step: 0.01, min: 0, max: 1 }}
                  sx={{ width: 110 }}
                />
              ) : (
                <Typography color="var(--muted)" sx={{ fontSize: 12 }}>
                  floor 0 (fixed)
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Box className="flex justify-end pt-4">
        <Button onClick={save} disabled={!dirty || saving} className="!px-6">
          <Typography>{saving ? 'Saving…' : 'Save bands'}</Typography>
        </Button>
      </Box>
    </Paper>
  );
};
